package fr.frux.feature.treeadd.wizard

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.treeadd.resources.Res
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_cancel
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_comment_label
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_comment_placeholder
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_dialog_title
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_error
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_name_label
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_quota_error
import fr.frux.feature.treeadd.resources.treeadd_species_suggest_send
import fr.frux.shared.content.SpeciesSuggestionLimits
import org.jetbrains.compose.resources.stringResource

/**
 * Boîte à suggestions d'espèces (#233) : journal libre, distinct du circuit structuré de
 * proposition. Le nom se pré-remplit avec la recherche en cours, la création de l'arbre n'est
 * jamais affectée par cette suggestion.
 */
@Composable
internal fun SuggestSpeciesDialog(
    initialName: String,
    sending: Boolean,
    error: AddTreeSuggestionError?,
    onDismiss: () -> Unit,
    onSubmit: (name: String, comment: String) -> Unit,
) {
    var name by remember { mutableStateOf(initialName.take(SpeciesSuggestionLimits.NAME_MAX)) }
    var comment by remember { mutableStateOf("") }

    AlertDialog(
        // #324 : un envoi en cours ne se ferme ni par un tap hors dialogue ni par le retour, la
        // requête part quand même et la confirmation arriverait sur un écran qui semblait fermé.
        onDismissRequest = { if (!sending) onDismiss() },
        title = { Text(stringResource(Res.string.treeadd_species_suggest_dialog_title)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
                FruxTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = stringResource(Res.string.treeadd_species_suggest_name_label),
                    maxLength = SpeciesSuggestionLimits.NAME_MAX,
                    enabled = !sending,
                    modifier = Modifier.fillMaxWidth(),
                )
                FruxTextField(
                    value = comment,
                    onValueChange = { comment = it },
                    label = stringResource(Res.string.treeadd_species_suggest_comment_label),
                    placeholder = stringResource(Res.string.treeadd_species_suggest_comment_placeholder),
                    maxLength = SpeciesSuggestionLimits.COMMENT_MAX,
                    singleLine = false,
                    enabled = !sending,
                    modifier = Modifier.fillMaxWidth(),
                )
                if (error != null) {
                    Text(
                        text = error.message(),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.error,
                    )
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = { onSubmit(name, comment) },
                enabled = name.isNotBlank() && !sending,
            ) {
                Text(stringResource(Res.string.treeadd_species_suggest_send))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !sending) {
                Text(stringResource(Res.string.treeadd_species_suggest_cancel))
            }
        },
    )
}

@Composable
private fun AddTreeSuggestionError.message(): String =
    when (this) {
        AddTreeSuggestionError.Network -> stringResource(Res.string.treeadd_species_suggest_error)
        AddTreeSuggestionError.Quota -> stringResource(Res.string.treeadd_species_suggest_quota_error)
    }
